/**
 * Auswertung einzelner Arbeitstage und die Zustandsmaschine des Stempelns.
 *
 * Diese Funktionen laufen wortgleich im Browser (Anzeige) und in der
 * Appwrite-Funktion (Validierung). Genau deshalb liegen sie hier und nicht
 * in einer der beiden Seiten.
 */

import { HOUR_MS, MINUTE_MS, type DayKey } from './time.ts';
import type { Break, DayEntry, StampAction, TerminalStatus } from './types.ts';

/** Summe aller Pausen. Eine laufende Pause zählt bis `now`. */
export function breakMs(entry: Pick<DayEntry, 'breaks'>, now: number): number {
  return (entry.breaks ?? []).reduce((sum, b) => sum + ((b.e ?? now) - b.s), 0);
}

/** Anwesenheit von Kommen bis Gehen, Pausen inklusive. */
export function grossMs(entry: Pick<DayEntry, 'clockIn' | 'clockOut'>, now: number): number {
  if (!entry?.clockIn) return 0;
  return (entry.clockOut ?? now) - entry.clockIn;
}

/** Arbeitszeit = Anwesenheit minus Pausen. Nie negativ. */
export function netMs(
  entry: Pick<DayEntry, 'clockIn' | 'clockOut' | 'breaks'> | null | undefined,
  now: number,
): number {
  if (!entry?.clockIn) return 0;
  return Math.max(0, grossMs(entry, now) - breakMs(entry, now));
}

export const hasRunningBreak = (entry: Pick<DayEntry, 'breaks'> | null | undefined): boolean =>
  (entry?.breaks ?? []).some((b) => b.e === null);

/**
 * Der Zustand, in dem die Terminal-Ansicht steht.
 *
 * `missing_out` (nächtlicher Auto-Abschluss) zählt bewusst als `done`: der Tag
 * ist vorbei und darf nicht weiterlaufen, er ist nur korrekturbedürftig.
 */
export function terminalStatus(entry: DayEntry | null | undefined): TerminalStatus {
  if (!entry?.clockIn) return 'off';
  if (entry.clockOut || entry.status === 'missing_out') return 'done';
  return hasRunningBreak(entry) ? 'break' : 'on';
}

/** Welche Stempel-Aktionen sind aus diesem Zustand heraus erlaubt? */
export function allowedActions(status: TerminalStatus): StampAction[] {
  switch (status) {
    case 'off': return ['clock_in'];
    case 'on': return ['break_start', 'clock_out'];
    case 'break': return ['break_end', 'clock_out'];
    case 'done': return ['clock_in'];
  }
}

export const canStamp = (status: TerminalStatus, action: StampAction): boolean =>
  allowedActions(status).includes(action);

/**
 * Wendet eine Stempelung auf einen Tag an. `now` kommt immer vom Server.
 *
 * Reine Funktion: gibt den neuen Tag zurück, verändert den alten nicht.
 * Wirft, wenn die Aktion im aktuellen Zustand nicht erlaubt ist — die
 * Funktion ist die letzte Instanz, der Browser nur die erste.
 */
export function applyStamp(
  entry: DayEntry | null,
  action: StampAction,
  now: number,
  ctx: { employeeId: string; date: DayKey },
): DayEntry {
  const status = terminalStatus(entry);
  if (!canStamp(status, action)) {
    throw new StampError(`Aktion "${action}" ist im Zustand "${status}" nicht möglich.`, status, action);
  }

  const base: DayEntry = entry
    ? { ...entry, breaks: entry.breaks.map((b) => ({ ...b })) }
    : {
        employeeId: ctx.employeeId,
        date: ctx.date,
        clockIn: null,
        clockOut: null,
        breaks: [],
        netMinutes: null,
        status: 'open',
        corrected: false,
        note: '',
      };

  switch (action) {
    case 'clock_in':
      // Erneutes Kommen am selben Tag setzt den Feierabend zurück; die
      // bisherigen Pausen bleiben stehen, die Zeit läuft weiter.
      base.clockIn = base.clockIn ?? now;
      base.clockOut = null;
      base.status = 'open';
      break;

    case 'break_start':
      base.breaks.push({ s: now, e: null });
      break;

    case 'break_end':
      closeOpenBreaks(base.breaks, now);
      break;

    case 'clock_out':
      // Gehen beendet eine laufende Pause mit — sonst liefe sie ewig weiter.
      closeOpenBreaks(base.breaks, now);
      base.clockOut = now;
      base.status = 'closed';
      break;
  }

  base.netMinutes = base.clockOut ? Math.round(netMs(base, now) / MINUTE_MS) : null;
  return base;
}

function closeOpenBreaks(breaks: Break[], now: number): void {
  for (const b of breaks) if (b.e === null) b.e = now;
}

export class StampError extends Error {
  readonly status: TerminalStatus;
  readonly action: StampAction;

  constructor(message: string, status: TerminalStatus, action: StampAction) {
    super(message);
    this.name = 'StampError';
    this.status = status;
    this.action = action;
  }
}

/**
 * Arbeitszeit eines Tages für Auswertungen.
 *
 * Abgeschlossene Tage nutzen das gespeicherte `netMinutes`, damit Wochen- und
 * Monatssummen nicht von `now` abhängen und rückwirkend stabil bleiben.
 */
export function settledNetMs(entry: DayEntry | null | undefined, now: number): number {
  if (!entry) return 0;
  if (entry.netMinutes !== null && entry.clockOut) return entry.netMinutes * MINUTE_MS;
  return netMs(entry, now);
}

/** Tages-Soll = Wochen-Soll auf die vereinbarten Arbeitstage verteilt. */
export function dailyTargetMs(weeklyTargetHours: number, workdaysPerWeek: number): number {
  const days = workdaysPerWeek > 0 ? workdaysPerWeek : 5;
  return (weeklyTargetHours * HOUR_MS) / days;
}
